import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  getOrganizationDocs,
  getOrganizationDocFileUrl,
} from '../../services/userService';

function OrganizationDocs() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const [selected, setSelected] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchDocs = async (opts = {}) => {
    setLoading(true);
    try {
      const nextPage = opts.page !== undefined ? opts.page : page;
      const nextLimit = opts.limit !== undefined ? opts.limit : limit;
      const nextSearch = opts.search !== undefined ? opts.search : debouncedSearch;
      const nextTag = opts.tag !== undefined ? opts.tag : '';

      const params = {
        page: nextPage,
        limit: nextLimit,
      };
      if (nextSearch) params.search = nextSearch;
      if (nextTag) params.tag = nextTag;

      const resp = await getOrganizationDocs(params);
      const list = resp?.data?.docs || resp?.data?.data?.docs || resp?.docs || [];
      setDocs(Array.isArray(list) ? list : []);

      const pg = resp?.data?.pagination || resp?.data?.data?.pagination;
      if (pg) {
        setPage(pg.page || nextPage);
        setLimit(pg.limit || nextLimit);
        setTotalPages(pg.totalPages || 1);
        setTotalCount(pg.totalCount || 0);
      } else {
        setPage(nextPage);
        setLimit(nextLimit);
        setTotalPages(1);
        setTotalCount(Array.isArray(list) ? list.length : 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.data?.error_msg || 'Failed to load organization docs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDocs({ page: 1, search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const allTags = useMemo(() => {
    const set = new Set();
    docs.forEach((d) => {
      if (Array.isArray(d.tags)) {
        d.tags.forEach((t) => {
          if (t) set.add(String(t));
        });
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [docs]);

  const filteredDocs = useMemo(() => {
    const sorted = [...docs];
    if (sortBy === 'oldest') {
      sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortBy === 'title_asc') {
      sorted.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    } else {
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return sorted;
  }, [docs, sortBy]);

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const openViewer = (d) => {
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;

    setSelected(d);
    setIsViewerOpen(true);

    const url = getOrganizationDocFileUrl(d.id);
    setPreviewUrl(url);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
    setSelected(null);
    setPreviewUrl(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Organization Docs</h1>
          <div className="text-sm text-gray-500">Shared documents for your organization</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          Showing {(page - 1) * limit + (filteredDocs.length ? 1 : 0)}
          {filteredDocs.length ? `-${(page - 1) * limit + filteredDocs.length}` : ''}
          {totalCount ? ` of ${totalCount}` : ''}
        </div>

        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setLimit(next);
              fetchDocs({ page: 1, limit: next });
            }}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </select>

          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-xs hover:bg-gray-50 disabled:opacity-50"
            onClick={() => fetchDocs({ page: Math.max(page - 1, 1) })}
            disabled={loading || page <= 1}
          >
            Prev
          </button>
          <div className="text-xs text-gray-600 px-2">
            Page {page} of {Math.max(totalPages, 1)}
          </div>
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-xs hover:bg-gray-50 disabled:opacity-50"
            onClick={() => fetchDocs({ page: Math.min(page + 1, totalPages) })}
            disabled={loading || page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or tags..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[160px]"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="title_asc">Title A-Z</option>
        </select>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500">No docs found.</div>
          ) : (
            <div className="divide-y">
              {filteredDocs.map((d) => (
                <div key={d.id} className="py-4 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{d.title}</div>
                      <div className="text-xs text-gray-500">{formatDate(d.createdAt)}</div>
                    </div>
                    {Array.isArray(d.tags) && d.tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {d.tags.slice(0, 8).map((t) => (
                          <span key={t} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => openViewer(d)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
                    >
                      <Icon icon="fluent:open-24-regular" width="18" height="18" />
                      View
                    </button>
                    <a
                      href={getOrganizationDocFileUrl(d.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
                    >
                      <Icon icon="fluent:arrow-download-24-regular" width="18" height="18" />
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isViewerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="min-w-0">
                <div className="text-lg font-semibold truncate">{selected?.title || 'Document'}</div>
                <div className="text-xs text-gray-500">Preview</div>
              </div>
              <div className="flex items-center gap-2">
                {selected?.id && (
                  <a
                    href={getOrganizationDocFileUrl(selected.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
                  >
                    <Icon icon="fluent:open-24-regular" width="18" height="18" />
                    Open
                  </a>
                )}
                <button
                  type="button"
                  className="p-2 rounded-lg hover:bg-gray-100"
                  onClick={closeViewer}
                >
                  <Icon icon="mdi:close" width="20" height="20" />
                </button>
              </div>
            </div>

            <div className="p-4">
              {previewUrl ? (
                <iframe
                  title="Document Preview"
                  src={previewUrl}
                  className="w-full h-[70vh] rounded-lg border"
                />
              ) : (
                <div className="text-center py-12 text-sm text-gray-500">No preview available.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationDocs;
